import React, { useState, useEffect, useCallback, useMemo } from 'react';
import client from '../../services/graphqlClient';
import { useNotification } from '../../context/NotificationContext';
import './Tecnico.css';

const MESES = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const MESES_CORTO = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const ESTADOS_LABEL = {
  'vendido': 'Vendido',
  'contratado_no_iniciado': 'Contratado',
  'en_ejecucion': 'En Ejecución',
  'cobrado_liquidado': 'Cobrado',
  'entregado_liquidado': 'Entregado'
};

const formatCurrency = (val) => {
  if (val === null || val === undefined || isNaN(val)) return '-';
  return '$ ' + Math.round(val).toLocaleString('es-CO');
};

const formatPct = (val) => {
  if (val === null || val === undefined || isNaN(val)) return '-';
  return (val * 100).toFixed(1) + '%';
};

const formatDate = (fecha) => {
  if (!fecha) return '-';
  const d = new Date(fecha + 'T12:00:00');
  return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function Tecnico() {
  const { error } = useNotification();
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [proyectos, setProyectos] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [activeTab, setActiveTab] = useState('cronograma');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroMes, setFiltroMes] = useState('todos');
  const [busqueda, setBusqueda] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await client.query(`query($anio: Int) { gerenciaTecnico(anio: $anio) { proyectos { id proyecto_nombre apartamento nombre_cliente estado valor_final fecha_creacion fecha_inicio_cron fecha_fin_cron dias_totales costo_real porcentaje_costo } resumen { total_proyectos en_ejecucion valor_total costo_total } } }`, { anio });
      const mapped = (data.gerenciaTecnico.proyectos || []).map(p => {
        const fechaInicio = p.fecha_inicio_cron || p.fecha_creacion;
        const mesInicio = fechaInicio ? new Date(fechaInicio).getMonth() + 1 : null;
        return { ...p, proyecto: p.proyecto_nombre, cliente: p.nombre_cliente, mes_inicio: mesInicio };
      });
      setProyectos(mapped);
      setResumen(data.gerenciaTecnico.resumen || null);
    } catch (err) {
      error('Error cargando KPI Técnico');
    } finally {
      setLoading(false);
    }
  }, [anio, error]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const proyectosFiltrados = useMemo(() => {
    let resultado = [...proyectos];
    if (filtroEstado !== 'todos') {
      resultado = resultado.filter(p => p.estado === filtroEstado);
    }
    if (filtroMes !== 'todos') {
      resultado = resultado.filter(p => p.mes_inicio === parseInt(filtroMes));
    }
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      resultado = resultado.filter(p =>
        (p.proyecto || '').toLowerCase().includes(q) ||
        (p.apartamento || '').toLowerCase().includes(q) ||
        (p.cliente || '').toLowerCase().includes(q)
      );
    }
    return resultado;
  }, [proyectos, filtroEstado, filtroMes, busqueda]);

  // Agrupar por mes
  const proyectosPorMes = useMemo(() => {
    const map = {};
    proyectosFiltrados.forEach(p => {
      if (!map[p.mes_inicio]) map[p.mes_inicio] = [];
      map[p.mes_inicio].push(p);
    });
    return map;
  }, [proyectosFiltrados]);

  const mesesConProyectos = useMemo(() => {
    return Object.keys(proyectosPorMes).map(Number).sort((a, b) => a - b);
  }, [proyectosPorMes]);

  // Estados únicos para filtro
  const estadosUnicos = useMemo(() => {
    return [...new Set(proyectos.map(p => p.estado))];
  }, [proyectos]);

  const getEstadoClass = (estado) => {
    const map = {
      'vendido': 'estado-vendido',
      'contratado_no_iniciado': 'estado-contratado',
      'en_ejecucion': 'estado-ejecucion',
      'cobrado_liquidado': 'estado-cobrado',
      'entregado_liquidado': 'estado-entregado'
    };
    return map[estado] || '';
  };

  const getCumplimientoClass = (val) => {
    if (val === null || val === undefined) return '';
    if (val >= 1) return 'kpi-verde';
    if (val >= 0.9) return 'kpi-amarillo';
    return 'kpi-rojo';
  };

  const getDiferenciaClass = (val) => {
    if (val === null || val === undefined) return '';
    if (val > 0) return 'kpi-verde';
    if (val === 0) return 'kpi-amarillo';
    return 'kpi-rojo';
  };

  if (loading) {
    return <div className="tec-loading"><div className="spinner"></div><p>Cargando KPI Técnico...</p></div>;
  }

  return (
    <div className="tecnico-module">
      {/* Header */}
      <div className="tec-header">
        <div className="tec-header-left">
          <h1>KPI Técnico</h1>
          <p>Indicadores de cronograma y presupuesto por proyecto</p>
        </div>
        <div className="tec-header-right">
          <select value={anio} onChange={e => setAnio(parseInt(e.target.value))} className="tec-anio-select">
            {Array.from({ length: new Date().getFullYear() - 2025 + 1 }, (_, i) => 2026 + i).map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Resumen Cards */}
      {resumen && (
        <div className="tec-resumen-cards">
          <div className="tec-card accent-blue">
            <span className="tec-card-value">{resumen.total_proyectos}</span>
            <span className="tec-card-label">Total Proyectos</span>
          </div>
          <div className="tec-card accent-green">
            <span className="tec-card-value">{resumen.en_ejecucion}</span>
            <span className="tec-card-label">En Ejecución</span>
          </div>
          <div className="tec-card accent-purple">
            <span className="tec-card-value">{resumen.finalizados}</span>
            <span className="tec-card-label">Finalizados</span>
          </div>
          <div className="tec-card accent-emerald">
            <span className="tec-card-value">{resumen.a_tiempo}</span>
            <span className="tec-card-label">A Tiempo</span>
          </div>
          <div className="tec-card accent-red">
            <span className="tec-card-value">{resumen.retrasados}</span>
            <span className="tec-card-label">Retrasados</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tec-tabs">
        <button className={`tec-tab ${activeTab === 'cronograma' ? 'active' : ''}`} onClick={() => setActiveTab('cronograma')}>
          KPI Cronograma
        </button>
        <button className={`tec-tab ${activeTab === 'presupuesto' ? 'active' : ''}`} onClick={() => setActiveTab('presupuesto')}>
          KPI Presupuesto
        </button>
      </div>

      {/* Filtros */}
      <div className="tec-filtros">
        <input type="text" className="tec-busqueda" placeholder="Buscar por proyecto, apto o cliente..." value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        <select className="tec-filtro-select" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
          <option value="todos">Todos los estados</option>
          {estadosUnicos.map(est => (
            <option key={est} value={est}>{ESTADOS_LABEL[est] || est}</option>
          ))}
        </select>
        <select className="tec-filtro-select" value={filtroMes} onChange={e => setFiltroMes(e.target.value)}>
          <option value="todos">Todos los meses</option>
          {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
            <option key={m} value={m}>{MESES[m]}</option>
          ))}
        </select>
      </div>

      {/* KPI CRONOGRAMA */}
      {activeTab === 'cronograma' && (
        <div className="tec-content">
          {mesesConProyectos.length === 0 ? (
            <p className="tec-empty">No hay proyectos para los filtros seleccionados</p>
          ) : (
            mesesConProyectos.map(mes => (
              <div key={mes} className="tec-mes-group">
                <div className="tec-mes-header">
                  <h3>{MESES[mes]} {anio}</h3>
                  <span className="tec-mes-count">{proyectosPorMes[mes].length} proyectos</span>
                </div>
                <div className="tec-table-wrapper">
                  <table className="tec-table">
                    <thead>
                      <tr>
                        <th>Proyecto</th>
                        <th>Apto</th>
                        <th>Estado</th>
                        <th>Fecha Inicio</th>
                        <th>Entrega Plan</th>
                        <th>Entrega Real</th>
                        <th>Días Esperados</th>
                        <th>Días Reales</th>
                        <th>Diferencia</th>
                        <th>Cumplimiento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {proyectosPorMes[mes].map(p => (
                        <tr key={p.id} className={p.es_finalizado ? 'row-finalizado' : ''}>
                          <td><strong>{p.proyecto}</strong></td>
                          <td>{p.apartamento || '-'}</td>
                          <td><span className={`tec-estado-badge ${getEstadoClass(p.estado)}`}>{ESTADOS_LABEL[p.estado] || p.estado}</span></td>
                          <td>{formatDate(p.fecha_inicio)}</td>
                          <td>{formatDate(p.fecha_fin_plan)}</td>
                          <td>{p.fecha_entrega_real ? formatDate(p.fecha_entrega_real) : <span className="tec-pendiente">En curso</span>}</td>
                          <td className="tec-num">{p.dias_esperados}</td>
                          <td className="tec-num">{p.dias_reales || '-'}</td>
                          <td className={`tec-num ${getDiferenciaClass(p.diferencia_dias)}`}>
                            {p.diferencia_dias !== null ? (p.diferencia_dias > 0 ? '+' : '') + p.diferencia_dias + 'd' : '-'}
                          </td>
                          <td className={`tec-num ${getCumplimientoClass(p.cumplimiento_tiempo)}`}>
                            {p.cumplimiento_tiempo !== null ? (p.cumplimiento_tiempo * 100).toFixed(0) + '%' : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* KPI PRESUPUESTO */}
      {activeTab === 'presupuesto' && (
        <div className="tec-content">
          {mesesConProyectos.length === 0 ? (
            <p className="tec-empty">No hay proyectos para los filtros seleccionados</p>
          ) : (
            mesesConProyectos.map(mes => (
              <div key={mes} className="tec-mes-group">
                <div className="tec-mes-header">
                  <h3>{MESES[mes]} {anio}</h3>
                  <span className="tec-mes-count">{proyectosPorMes[mes].length} proyectos</span>
                </div>
                <div className="tec-table-wrapper">
                  <table className="tec-table">
                    <thead>
                      <tr>
                        <th>Proyecto</th>
                        <th>Apto</th>
                        <th>Estado</th>
                        <th>Presupuesto</th>
                        <th>Costo Real</th>
                        <th>Utilidad Esp. (25%)</th>
                        <th>Utilidad Real %</th>
                        <th>Utilidad Real $</th>
                        <th>Diferencia %</th>
                        <th>Diferencia $</th>
                        <th>Cumplimiento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {proyectosPorMes[mes].map(p => (
                        <tr key={p.id} className={p.es_finalizado ? 'row-finalizado' : ''}>
                          <td><strong>{p.proyecto}</strong></td>
                          <td>{p.apartamento || '-'}</td>
                          <td><span className={`tec-estado-badge ${getEstadoClass(p.estado)}`}>{ESTADOS_LABEL[p.estado] || p.estado}</span></td>
                          <td className="tec-num">{formatCurrency(p.presupuesto)}</td>
                          <td className="tec-num">{p.costo_real > 0 ? formatCurrency(p.costo_real) : '-'}</td>
                          <td className="tec-num">{formatPct(p.utilidad_esperada_pct)}</td>
                          <td className={`tec-num ${getCumplimientoClass(p.cumplimiento_presupuesto)}`}>
                            {p.costo_real > 0 ? formatPct(p.utilidad_real_pct) : '-'}
                          </td>
                          <td className={`tec-num ${p.utilidad_real_dinero >= 0 ? 'kpi-verde' : 'kpi-rojo'}`}>
                            {p.costo_real > 0 ? formatCurrency(p.utilidad_real_dinero) : '-'}
                          </td>
                          <td className={`tec-num ${getDiferenciaClass(p.diferencia_pct)}`}>
                            {p.costo_real > 0 ? formatPct(p.diferencia_pct) : '-'}
                          </td>
                          <td className={`tec-num ${p.diferencia_dinero >= 0 ? 'kpi-verde' : 'kpi-rojo'}`}>
                            {p.costo_real > 0 ? formatCurrency(p.diferencia_dinero) : '-'}
                          </td>
                          <td className={`tec-num bold ${getCumplimientoClass(p.cumplimiento_presupuesto)}`}>
                            {p.costo_real > 0 ? (p.cumplimiento_presupuesto * 100).toFixed(0) + '%' : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
